// src/odoo/helpdesk_dashboard/pages/HelpdeskDashboardPage.tsx
// All comments are in English.

import React from "react";
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  CircleDot,
  CircleSlash2,
  ChevronsUpDown,
  FilterX,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/shared/PageHeader";
import { cn } from "@/lib/utils";
import {
  useHelpdeskAssigneesMonthly,
  useHelpdeskAssigneesOverall,
  useHelpdeskIssueTypes,
  useHelpdeskMonthlyTrend,
  useHelpdeskOpenAging,
  useHelpdeskSalespersonsOverall,
  useHelpdeskStageDistribution,
  useHelpdeskSubTypes,
  useHelpdeskSummary,
  useHelpdeskTickets,
  useHelpdeskTopCustomers,
  useHelpdeskTopProducts,
} from "../hooks/useHelpdeskDashboard";
import type {
  AssigneeMonthlyPoint,
  CustomerIssueTypePoint,
  HelpdeskDashboardFilters,
  HelpdeskTicketDrilldownFilters,
  MonthlyTrendPoint,
  MonthlyTrendTicketInfo,
  NamedCountPoint,
  OpenAgingPoint,
} from "../api/helpdeskDashboardApi";
import SharedTable from "@/components/shared/SharedTable";

type FiltersState = HelpdeskDashboardFilters;

type ComboboxFilterProps = {
  label: string;
  value?: string;
  options: string[];
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  onSelect: (value: string | undefined) => void;
};

const SERIES_COLORS = [
  "#0f766e",
  "#2563eb",
  "#7c3aed",
  "#ea580c",
  "#16a34a",
  "#dc2626",
  "#0ea5e9",
  "#9333ea",
  "#d97706",
  "#475569",
];

const formatCount = (value: number | null | undefined): string =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    value ?? 0
  );

const formatPercent = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return "0%";
  return `${Math.round(value)}%`;
};

const toDateInputValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultFilters = (): FiltersState => {
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);
  return {
    startDate: toDateInputValue(startOfYear),
    endDate: toDateInputValue(today),
    assignee: "All",
    salesperson: "All",
    customer: "All",
    stage: "All",
    ticketType: "All",
    tags: "All",
  };
};

const normalizeText = (value: string | undefined): string | undefined => {
  const next = value?.trim();
  return next ? next : undefined;
};

const normalizeSelection = (value: string | undefined): string | undefined => {
  if (!value || value === "All") return undefined;
  return normalizeText(value);
};

const uniqueSortedValues = (values: Array<string | null | undefined>): string[] =>
  Array.from(
    new Set(
      values
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))
    )
  ).sort((left, right) => left.localeCompare(right));

const formatMonthLabel = (month: string): string => {
  const [year, monthPart] = month.split("-");
  const parsedYear = Number(year);
  const parsedMonth = Number(monthPart);
  if (!Number.isFinite(parsedYear) || !Number.isFinite(parsedMonth)) {
    return month;
  }
  return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(
    new Date(parsedYear, parsedMonth - 1, 1)
  );
};

const formatDateTimeLabel = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const buildOdooTicketUrl = (ticketId: number): string =>
  `https://forida.odoo.com/web#id=${ticketId}&cids=1-2-3&menu_id=516&action=741&model=helpdesk.ticket&view_type=form`;

type TicketDrilldownState = {
  title: string;
  filters: HelpdeskTicketDrilldownFilters;
};

const normalizePieKey = (value: string | undefined): string =>
  normalizeText(value)?.toLowerCase() || "unknown";

const STAGE_COLOR_MAP: Record<string, string> = {
  new: "#156082",
  "in progress": "#E97132",
  solved: "#196B24",
  canceled: "#A6A6A6",
  cancelled: "#A6A6A6",
  unknown: "#808080",
};

const ISSUE_TYPE_COLOR_MAP: Record<string, string> = {
  license: "#156082",
  "product issue": "#E97132",
  "question/enquiry": "#196B24",
  unknown: "#808080",
};

const EXCEL_PIE_COLORS = [
  "#156082",
  "#E97132",
  "#196B24",
  "#0F9ED5",
  "#A02B93",
  "#4EA72E",
  "#0D3A4E",
  "#994010",
  "#E6B8B7",
  "#7F7F7F",
];

type PieLabelProps = {
  cx?: number;
  cy?: number;
  midAngle?: number;
  outerRadius?: number;
  percent?: number;
  name?: string;
};

type CustomerStackedRow = {
  customer: string;
  License: number;
  "Product Issue": number;
  "Question/Enquiry": number;
  total: number;
};

type AssignmentActivityRow = {
  month: string;
  month_key: string;
  total: number;
  [assignee: string]: string | number;
};

const CUSTOMER_STACK_SERIES = [
  "License",
  "Product Issue",
  "Question/Enquiry",
] as const;

const CUSTOMER_STACK_COLORS: Record<(typeof CUSTOMER_STACK_SERIES)[number], string> = {
  License: "#156082",
  "Product Issue": "#E97132",
  "Question/Enquiry": "#196B24",
};

const ASSIGNMENT_ACTIVITY_COLORS = [
  "#156082",
  "#E97132",
  "#196B24",
  "#0F9ED5",
  "#A02B93",
  "#4EA72E",
];

const OVERDUE_BUCKET_ORDER = [
  "1-30",
  "31-60",
  "61-90",
  "91-120",
  "121-150",
  "151-180",
];

const OVERDUE_ASSIGNEE_COLORS: Record<string, string> = {
  "Calvin Chan": "#156082",
  "Dicky Chung": "#E97132",
  KoeyChu: "#196B24",
};

const OVERDUE_ASSIGNEE_FALLBACK_COLORS = [
  "#0F9ED5",
  "#A02B93",
  "#4EA72E",
  "#0D3A4E",
  "#994010",
];

const ComboboxFilter: React.FC<ComboboxFilterProps> = ({
  label,
  value,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  onSelect,
}) => {
  const [open, setOpen] = React.useState(false);
  const selectedLabel = value ?? "All";

  const handleSelect = (nextValue: string) => {
    onSelect(nextValue === "All" ? undefined : nextValue);
    setOpen(false);
  };

  return (
    <label className="space-y-2 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-9 w-full justify-between text-sm"
          >
            {selectedLabel || placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="min-w-[320px] w-auto p-0" align="start">
          <Command>
            <CommandInput placeholder={searchPlaceholder} className="h-9 text-xs" />
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandList>
              <CommandGroup>
                {["All", ...options].map((option) => {
                  const isSelected = option === selectedLabel;
                  return (
                    <CommandItem
                      key={option}
                      value={option}
                      onSelect={() => handleSelect(option)}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Check className={cn("h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
                        <span className="truncate">{option}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </label>
  );
};

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const normalizeCustomerTicketType = (value: string): (typeof CUSTOMER_STACK_SERIES)[number] => {
  const normalized = normalizePieKey(value);
  if (normalized.includes("license")) return "License";
  if (normalized.includes("product issue")) return "Product Issue";
  return "Question/Enquiry";
};

const getMonthLabel = (monthKey: string): string => {
  const [yearPart, monthPart] = monthKey.split("-");
  const monthIndex = Number(monthPart) - 1;
  const monthName = MONTH_ABBREVIATIONS[monthIndex] ?? monthKey;
  if (!yearPart || Number.isNaN(monthIndex)) return monthKey;
  return monthName;
};

const getStageColor = (label: string): string => {
  const normalized = normalizePieKey(label);
  if (normalized.includes("in progress")) return STAGE_COLOR_MAP["in progress"];
  if (normalized.includes("solved")) return STAGE_COLOR_MAP.solved;
  if (normalized.includes("cancelled")) return STAGE_COLOR_MAP.cancelled;
  if (normalized.includes("canceled")) return STAGE_COLOR_MAP.canceled;
  if (normalized.includes("new")) return STAGE_COLOR_MAP.new;
  return STAGE_COLOR_MAP.unknown;
};

const getIssueTypeColor = (label: string): string => {
  const normalized = normalizePieKey(label);
  if (normalized.includes("license")) return ISSUE_TYPE_COLOR_MAP.license;
  if (normalized.includes("product issue")) return ISSUE_TYPE_COLOR_MAP["product issue"];
  if (normalized.includes("question") || normalized.includes("enquiry")) {
    return ISSUE_TYPE_COLOR_MAP["question/enquiry"];
  }
  return ISSUE_TYPE_COLOR_MAP.unknown;
};

const getExcelPieColor = (index: number): string =>
  EXCEL_PIE_COLORS[index % EXCEL_PIE_COLORS.length];

const getNamedCountLabel = (row: { label?: string; tag_name?: string }): string =>
  String(row.label ?? row.tag_name ?? "Unknown");

const renderPieLabel = ({
  cx = 0,
  cy = 0,
  midAngle = 0,
  outerRadius = 0,
  percent = 0,
  name = "",
}: PieLabelProps) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 36;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={10}
      fontWeight={500}
      fill="#475569"
    >
      <tspan x={x} dy="-0.4em">
        {name}
      </tspan>
      <tspan x={x} dy="1.2em">
        {formatPercent(percent * 100)}
      </tspan>
    </text>
  );
};

const createPieLabelRenderer = ({
  offset = 36,
  maxLabelLength,
  fontSize = 10,
}: {
  offset?: number;
  maxLabelLength?: number;
  fontSize?: number;
} = {}) => {
  return ({
    cx = 0,
    cy = 0,
    midAngle = 0,
    outerRadius = 0,
    percent = 0,
    name = "",
  }: PieLabelProps) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + offset;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const label =
      maxLabelLength && name.length > maxLabelLength
        ? `${name.slice(0, Math.max(0, maxLabelLength - 3))}...`
        : name;

    return (
      <text
        x={x}
        y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={fontSize}
      fontWeight={500}
      fill="#475569"
    >
        <tspan x={x} dy="-0.4em">
          {label}
        </tspan>
        <tspan x={x} dy="1.2em">
          {formatPercent(percent * 100)}
        </tspan>
      </text>
    );
  };
};

type SmartPieLabelPlacement = {
  label: string;
  x: number;
  y: number;
  anchor: "start" | "end";
  lineStartX: number;
  lineStartY: number;
  lineEndX: number;
  lineEndY: number;
};

const estimateLabelWidth = (label: string, percentLabel: string, fontSize = 10): number =>
  Math.ceil(Math.max(label.length, percentLabel.length) * fontSize * 0.64) + 8;

const buildSmartPieLabelPlacements = (
  rows: NamedCountPoint[],
  options: {
    width: number;
    height: number;
    outerRadius: number;
    labelOffset: number;
    topMargin: number;
    rightMargin: number;
    bottomMargin: number;
    leftMargin: number;
    minSpacing: number;
  }
): SmartPieLabelPlacement[] => {
  const total = rows.reduce((sum, row) => sum + Number(row.ticket_count ?? 0), 0);
  if (!total || options.width <= 0 || options.height <= 0) return [];

  const RADIAN = Math.PI / 180;
  const centerX = options.leftMargin + (options.width - options.leftMargin - options.rightMargin) / 2;
  const centerY = options.topMargin + (options.height - options.topMargin - options.bottomMargin) / 2;
  let currentAngle = 90;

  const rawPlacements = rows
    .map((row) => {
      const label = String(row.label ?? "");
      const value = Number(row.ticket_count ?? 0);
      if (value <= 0) return null;

      const percent = (value / total) * 100;
      const sweep = (value / total) * 360;
      const midAngle = currentAngle - sweep / 2;
      currentAngle -= sweep;
      const angle = -midAngle * RADIAN;
      const side: "start" | "end" = Math.cos(angle) >= 0 ? "start" : "end";
      const radial = options.outerRadius + options.labelOffset;
      const baseY = centerY + radial * Math.sin(angle);
      const lineStartX = centerX + options.outerRadius * Math.cos(angle);
      const lineStartY = centerY + options.outerRadius * Math.sin(angle);
      const textWidth = estimateLabelWidth(label, formatPercent(percent), 10);
      const baseX =
        side === "start"
          ? centerX + options.outerRadius + options.labelOffset
          : centerX - options.outerRadius - options.labelOffset;
      const x =
        side === "start"
          ? Math.min(
              options.width - options.rightMargin - textWidth,
              Math.max(baseX, options.leftMargin + 8)
            )
          : Math.max(
              options.leftMargin + textWidth,
              Math.min(baseX, options.width - options.rightMargin - 8)
            );

      return {
        label,
        x,
        y: baseY,
        anchor: side,
        lineStartX,
        lineStartY,
        lineEndX: side === "start" ? x - 6 : x + 6,
        lineEndY: baseY,
      };
    })
    .filter((item): item is SmartPieLabelPlacement => Boolean(item));

  const topBound = options.topMargin + 12;
  const bottomBound = options.height - options.bottomMargin - 12;
  const resolveSide = (side: "start" | "end") => {
    const items = rawPlacements.filter((item) => item.anchor === side).sort((a, b) => a.y - b.y);
    if (!items.length) return;

    let previousY = topBound;
    for (const item of items) {
      item.y = Math.max(item.y, previousY + options.minSpacing);
      previousY = item.y;
    }

    if (previousY > bottomBound) {
      const shiftUp = previousY - bottomBound;
      for (const item of items) {
        item.y -= shiftUp;
      }
      let nextY = bottomBound;
      for (let index = items.length - 1; index >= 0; index -= 1) {
        const item = items[index];
        item.y = Math.min(item.y, nextY - options.minSpacing);
        nextY = item.y;
      }
    }

    for (const item of items) {
      item.y = Math.max(topBound, Math.min(bottomBound, item.y));
      item.lineEndY = item.y;
      item.lineEndX = item.anchor === "start" ? item.x - 6 : item.x + 6;
    }
  };

  resolveSide("start");
  resolveSide("end");

  return rawPlacements;
};

const renderPieTooltip = (
  rows: NamedCountPoint[],
  title: string,
  active: boolean | undefined,
  payload: any
) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as { label?: string; ticket_count?: number } | undefined;
  if (!row) return null;

  const label = String(row.label ?? "");
  const value = Number(row.ticket_count ?? 0);
  const total = rows.reduce((sum, item) => sum + (item.ticket_count ?? 0), 0);
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
      <div className="text-sm font-semibold text-slate-900">{label}</div>
      <div className="mt-2 space-y-1 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-500">Ticket count</span>
          <span className="font-medium text-slate-900">{formatCount(value)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-slate-500">Share</span>
          <span className="font-medium text-slate-900">{formatPercent(percentage)}</span>
        </div>
      </div>
      <div className="mt-2 border-t pt-2 text-[11px] uppercase tracking-[0.16em] text-slate-500">
        {title}
      </div>
    </div>
  );
};

const buildSeries = <
  T extends Record<string, string | number | undefined>
>(
  rows: T[],
  xKey: keyof T,
  seriesKey: keyof T,
  valueKey: keyof T,
  topLimit: number,
  xOrder?: string[]
) => {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const label = String(row[seriesKey] ?? "Other");
    totals.set(label, (totals.get(label) ?? 0) + Number(row[valueKey] ?? 0));
  }

  const series = Array.from(totals.entries())
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .slice(0, topLimit)
    .map(([label]) => label);
  const useOther = totals.size > series.length;
  const finalSeries = useOther ? [...series, "Other"] : series;

  const bucketMap = new Map<string, Record<string, string | number>>();
  for (const row of rows) {
    const xLabel = String(row[xKey] ?? "Unknown");
    const seriesLabel = String(row[seriesKey] ?? "Other");
    const targetSeries = series.includes(seriesLabel) ? seriesLabel : "Other";
    const current = bucketMap.get(xLabel) ?? { [String(xKey)]: xLabel };
    current[targetSeries] = Number(current[targetSeries] ?? 0) + Number(row[valueKey] ?? 0);
    bucketMap.set(xLabel, current);
  }

  const chartRows = Array.from(bucketMap.values()).map((row) => {
    const next = { ...row };
    for (const label of finalSeries) {
      if (!(label in next)) {
        next[label] = 0;
      }
    }
    return next;
  });

  if (xOrder?.length) {
    chartRows.sort(
      (left, right) =>
        xOrder.indexOf(String(left[String(xKey)])) -
        xOrder.indexOf(String(right[String(xKey)]))
    );
  } else {
    chartRows.sort((left, right) =>
      String(left[String(xKey)]).localeCompare(String(right[String(xKey)]))
    );
  }

  return { chartRows, series: finalSeries };
};

const DonutCard: React.FC<{
  title: string;
  description: string;
  data: NamedCountPoint[];
  isLoading: boolean;
  error: Error | null;
  emptyMessage: string;
  className?: string;
  palette?: "stage" | "issue-type" | "generic";
  showLegend?: boolean;
  showLabels?: boolean;
  smartLabelLayout?: boolean;
  labelOffset?: number;
  labelMaxLength?: number;
  labelFontSize?: number;
  pieMargins?: { top?: number; right?: number; bottom?: number; left?: number };
  pieOuterRadius?: number;
  legendCompact?: boolean;
  legendFontSize?: number;
  legendPanelWidth?: number;
  balancedLayout?: boolean;
  legendOnRight?: boolean;
  onSliceClick?: (label: string) => void;
}> = ({
  title,
  description,
  data,
  isLoading,
  error,
  emptyMessage,
  className,
  palette = "generic",
  showLegend = true,
  showLabels = true,
  smartLabelLayout = false,
  labelOffset = 36,
  labelMaxLength,
  labelFontSize = 10,
  pieMargins,
  pieOuterRadius,
  legendCompact = false,
  legendFontSize = 10,
  legendPanelWidth = 220,
  balancedLayout = false,
  legendOnRight = false,
  onSliceClick,
}) => {
  const cardClassName = `border-slate-200/70 bg-white/85 shadow-sm backdrop-blur ${className ?? ""}`;
  const pickColor = (label: string, index: number): string => {
    if (palette === "stage") return getStageColor(label);
    if (palette === "issue-type") return getIssueTypeColor(label);
    return getExcelPieColor(index);
  };
  const renderLabel = createPieLabelRenderer({
    offset: labelOffset,
    maxLabelLength: labelMaxLength,
    fontSize: labelFontSize,
  });
  const longestLabelLength = Math.max(0, ...data.map((row) => String(row.label ?? "").length));
  const balancedChartHeight = balancedLayout ? Math.max(336, 314 + Math.min(28, data.length * 4)) : 320;
  const chartMargin = {
    top: pieMargins?.top ?? (smartLabelLayout ? Math.max(32, Math.min(56, 24 + Math.min(16, data.length * 2))) : 24),
    right: pieMargins?.right ?? 24,
    bottom: pieMargins?.bottom ?? (smartLabelLayout ? Math.max(32, Math.min(56, 24 + Math.min(16, data.length * 2))) : 24),
    left: pieMargins?.left ?? 24,
  };
  const computedOuterRadius = balancedLayout
    ? Math.max(
        68,
        Math.min(
          84,
          Math.round(
            (pieOuterRadius ?? 86) -
              Math.min(12, Math.max(0, longestLabelLength - 10) * 0.4) -
              Math.min(6, Math.max(0, data.length - 4) * 1.25)
          )
        )
      )
    : pieOuterRadius ?? (smartLabelLayout ? 92 : 96);
  const legendItems = data.map((entry, index) => ({
    label: entry.label,
    color: pickColor(entry.label, index),
  }));

  if (isLoading) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load data.</span>
        </CardContent>
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cardClassName}>
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {legendOnRight ? (
          <div
            className="flex flex-col gap-4 xl:flex-row xl:items-center"
            style={balancedLayout ? { height: balancedChartHeight } : undefined}
          >
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <div className={balancedLayout ? "h-full w-full max-w-[720px]" : "h-[320px] w-full max-w-[720px]"}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={chartMargin}>
                    <Pie
                      data={data}
                      dataKey="ticket_count"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={computedOuterRadius}
                      label={showLabels ? renderLabel : undefined}
                      labelLine={showLabels ? { stroke: "#94a3b8", strokeWidth: 1 } : undefined}
                      paddingAngle={2}
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={`${entry.label}-${index}`}
                          fill={pickColor(entry.label, index)}
                          className={onSliceClick ? "cursor-pointer" : undefined}
                          onClick={() => onSliceClick?.(entry.label)}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      allowEscapeViewBox={{ x: true, y: true }}
                      offset={balancedLayout ? 20 : 12}
                      wrapperStyle={{ pointerEvents: "none", zIndex: 20 }}
                      content={({ active, payload }) => renderPieTooltip(data, title, active, payload)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            {showLegend ? (
              <div
                className={`w-full min-w-0 rounded-2xl border border-slate-200/70 bg-slate-50/70 px-3 py-3 shadow-inner xl:w-[var(--legend-panel-width)] xl:flex-shrink-0 ${
                  legendCompact ? "text-[10px]" : "text-[11px]"
                }`}
                style={{ ["--legend-panel-width" as string]: `${legendPanelWidth}px` }}
              >
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Legend
                </div>
                <div
                  className={`flex flex-wrap gap-x-4 gap-y-2 text-slate-500 xl:flex-col xl:flex-nowrap xl:gap-2 ${
                    legendCompact ? "leading-none" : "leading-tight"
                  }`}
                  style={{ fontSize: `${legendFontSize}px` }}
                >
                  {legendItems.map((entry) => (
                    <div key={String(entry.label)} className="flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="truncate">{entry.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div style={balancedLayout ? { height: balancedChartHeight } : undefined}>
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={chartMargin}>
                  <Pie
                    data={data}
                    dataKey="ticket_count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={computedOuterRadius}
                    label={showLabels ? renderLabel : undefined}
                    labelLine={showLabels ? { stroke: "#94a3b8", strokeWidth: 1 } : undefined}
                    paddingAngle={2}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`${entry.label}-${index}`}
                        fill={pickColor(entry.label, index)}
                        className={onSliceClick ? "cursor-pointer" : undefined}
                        onClick={() => onSliceClick?.(entry.label)}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    allowEscapeViewBox={{ x: true, y: true }}
                    offset={balancedLayout ? 20 : 12}
                    wrapperStyle={{ pointerEvents: "none", zIndex: 20 }}
                    content={({ active, payload }) => renderPieTooltip(data, title, active, payload)}
                  />
                  {showLegend ? (
                    legendCompact ? (
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        height={56}
                        content={({ payload }) => (
                          <div className="flex w-full flex-nowrap items-center justify-between gap-1 overflow-hidden px-1 pt-5 text-[10px] leading-none text-slate-500">
                            {payload?.map((entry) => (
                              <div key={String(entry.value)} className="flex min-w-0 items-center gap-1 whitespace-nowrap">
                                <span
                                  className="h-2 w-2 shrink-0 rounded-full"
                                  style={{ backgroundColor: String(entry.color ?? "#94a3b8") }}
                                />
                                <span className="whitespace-nowrap">{String(entry.value)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      />
                    ) : (
                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        height={44}
                        wrapperStyle={{ bottom: 0, left: 0, right: 0, paddingTop: 8 }}
                      />
                    )
                  ) : null}
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

type ProductPieSlice = {
  label: string;
  ticket_count: number;
  percentage: number;
  isOther?: boolean;
};

type ProductBreakdownItem = {
  label: string;
  ticket_count: number;
  percentage: number;
};

type ProductSplitResult = {
  total: number;
  topItems: ProductPieSlice[];
  otherItems: ProductBreakdownItem[];
  pieData: ProductPieSlice[];
  otherTotal: number;
  otherPercentage: number;
};

const sortProductsByTicketCount = (rows: NamedCountPoint[]): NamedCountPoint[] =>
  [...rows].sort(
    (left, right) =>
      Number(right.ticket_count ?? 0) - Number(left.ticket_count ?? 0) ||
      getNamedCountLabel(left).localeCompare(getNamedCountLabel(right))
  );

const calculatePercentage = (value: number, total: number): number => (total > 0 ? (value / total) * 100 : 0);

const splitTopProductsByTicketCount = (rows: NamedCountPoint[], topLimit = 4): ProductSplitResult => {
  const sortedData = sortProductsByTicketCount(rows);
  const total = sortedData.reduce((sum, item) => sum + Number(item.ticket_count ?? 0), 0);
  const topItems = sortedData.slice(0, topLimit).map((item) => {
    const value = Number(item.ticket_count ?? 0);
    return {
      label: getNamedCountLabel(item),
      ticket_count: value,
      percentage: calculatePercentage(value, total),
    };
  });
  const otherItems = sortedData.slice(topLimit).map((item) => {
    const value = Number(item.ticket_count ?? 0);
    return {
      label: getNamedCountLabel(item),
      ticket_count: value,
      percentage: calculatePercentage(value, total),
    };
  });
  const otherTotal = otherItems.reduce((sum, item) => sum + item.ticket_count, 0);
  const otherPercentage = calculatePercentage(otherTotal, total);
  const pieData: ProductPieSlice[] = [
    ...topItems,
    ...(otherTotal > 0
      ? [{ label: "Other", ticket_count: otherTotal, percentage: otherPercentage, isOther: true }]
      : []),
  ].filter((item) => item.ticket_count > 0);

  return { total, topItems, otherItems, pieData, otherTotal, otherPercentage };
};

const TopProductsPieCard: React.FC<{
  title: string;
  description: string;
  data: NamedCountPoint[];
  isLoading: boolean;
  error: Error | null;
  emptyMessage: string;
  className?: string;
  onSliceClick?: (label: string) => void;
}> = ({ title, description, data, isLoading, error, emptyMessage, className, onSliceClick }) => {
  const cardClassName = `border-slate-200/70 bg-white/85 shadow-sm backdrop-blur ${className ?? ""}`;
  const split = React.useMemo(() => splitTopProductsByTicketCount(data, 4), [data]);

  if (isLoading) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load data.</span>
        </CardContent>
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  const renderProductPieLabel = ({
    cx = 0,
    cy = 0,
    midAngle = 0,
    outerRadius = 0,
    percent = 0,
    name = "",
  }: PieLabelProps) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 36;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const label = String(name);
    return (
      <text
        x={x}
        y={y}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={10}
        fontWeight={500}
        fill="#475569"
      >
        <tspan x={x} dy="-0.4em">
          {label}
        </tspan>
        <tspan x={x} dy="1.2em">
          {formatPercent(percent * 100)}
        </tspan>
      </text>
    );
  };

  const otherSegments = split.otherItems.map((item, index) => ({
    ...item,
    color: EXCEL_PIE_COLORS[(split.topItems.length + index) % EXCEL_PIE_COLORS.length],
  }));
  const otherBarTop = 0;
  const otherBarHeight = 100;
  const otherBarBottom = otherBarTop + otherBarHeight;
  const otherSegmentLayout = (() => {
    let currentTop = otherBarTop;
    return otherSegments.map((item, index) => {
      const shareOfOther = split.otherTotal > 0 ? item.ticket_count / split.otherTotal : 0;
      const rawHeight = shareOfOther * otherBarHeight;
      const height = index === otherSegments.length - 1 ? otherBarBottom - currentTop : rawHeight;
      const top = currentTop;
      currentTop += height;
      return {
        ...item,
        top,
        height,
        center: top + height / 2,
      };
    });
  })();
  const labelFontSize = Math.max(9, Math.min(11, Math.floor(12 - Math.max(0, Math.floor((Math.max(0, ...otherSegments.map((item) => item.label.length)) - 12) / 6)))));

  const pieTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload?: ProductPieSlice }> }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    return (
      <div className="rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
        <div className="text-sm font-semibold text-slate-900">{row.label}</div>
        <div className="mt-1 text-xs text-slate-500">
          Ticket count: <span className="font-medium text-slate-900">{formatCount(row.ticket_count)}</span>
        </div>
        <div className="text-xs text-slate-500">
          Share: <span className="font-medium text-slate-900">{formatPercent(row.percentage)}</span>
        </div>
      </div>
    );
  };

  return (
    <Card className={cardClassName}>
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:gap-3">
          <div className="min-w-0 overflow-visible xl:basis-[62%]">
            <div className="h-[300px] xl:h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 24, right: 40, bottom: 24, left: 40 }}>
                  <Pie
                    data={split.pieData}
                    dataKey="ticket_count"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={126}
                    label={renderProductPieLabel}
                    labelLine={{ stroke: "#94a3b8", strokeWidth: 1 }}
                    paddingAngle={2}
                  >
                    {split.pieData.map((entry, index) => (
                      <Cell
                        key={`${entry.label}-${index}`}
                        fill={EXCEL_PIE_COLORS[index % EXCEL_PIE_COLORS.length]}
                        className={onSliceClick ? "cursor-pointer" : undefined}
                        onClick={() => onSliceClick?.(entry.label)}
                      />
                    ))}
                  </Pie>
                  <Tooltip allowEscapeViewBox={{ x: true, y: true }} offset={12} content={pieTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="min-w-0 xl:basis-[38%]">
            {split.otherItems.length ? (
              <div className="flex h-[300px] flex-col xl:h-[340px]">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Other Breakdown
                  </div>
                  <div className="text-[11px] text-slate-500">{formatPercent(split.otherPercentage)} of total</div>
                </div>
                <div className="relative min-h-0 flex-1 overflow-visible">
                  <div className="absolute left-0 top-0 h-full w-14 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/70 shadow-inner xl:w-16">
                    <div className="flex h-full flex-col">
                      {otherSegmentLayout.map((item) => (
                        <div
                          key={item.label}
                          className="w-full"
                          style={{
                            flex: `${item.ticket_count} 1 0%`,
                            backgroundColor: item.color,
                          }}
                          onClick={() => onSliceClick?.(item.label)}
                        />
                      ))}
                    </div>
                  </div>
                  {otherSegmentLayout.map((item) => (
                    <React.Fragment key={item.label}>
                      <button
                        type="button"
                        className="absolute left-[72px] right-0 -translate-y-1/2 text-left xl:left-[88px]"
                        style={{ top: `${item.center}%` }}
                        onClick={() => onSliceClick?.(item.label)}
                      >
                        <span
                          className="block min-w-0 truncate font-medium text-slate-700"
                          style={{ fontSize: `${labelFontSize}px` }}
                        >
                          {item.label}, {formatCount(item.ticket_count)} ({formatPercent(item.percentage)})
                        </span>
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex h-[300px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 text-sm text-muted-foreground xl:h-[340px]">
                No remaining products to break down.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CustomerStackedBarCard: React.FC<{
  title: string;
  description: string;
  rows: CustomerStackedRow[];
  isLoading: boolean;
  error: Error | null;
  emptyMessage: string;
  className?: string;
  onBarClick?: (row: CustomerStackedRow, series: string) => void;
}> = ({ title, description, rows, isLoading, error, emptyMessage, className, onBarClick }) => {
  const cardClassName = `border-slate-200/70 bg-white/85 shadow-sm backdrop-blur ${className ?? ""}`;

  if (isLoading) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load data.</span>
        </CardContent>
      </Card>
    );
  }

  if (!rows.length) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  const renderTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload?: CustomerStackedRow }>;
  }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;

    return (
      <div className="rounded-xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
        <div className="text-sm font-semibold text-slate-900">{row.customer}</div>
        <div className="mt-2 space-y-1 text-sm">
          {CUSTOMER_STACK_SERIES.map((seriesName) => (
            <div key={seriesName} className="flex items-center justify-between gap-4">
              <span className="text-slate-500">{seriesName}</span>
              <span className="font-medium text-slate-900">{formatCount(row[seriesName])}</span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 border-t pt-2">
            <span className="text-slate-500">Total</span>
            <span className="font-semibold text-slate-900">{formatCount(row.total)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className={cardClassName}>
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[360px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 20, right: 32, left: 32, bottom: 52 }}
              barCategoryGap="28%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tick={{ fill: "#64748b", fontSize: 12 }}
              />
              <YAxis
                type="category"
                dataKey="customer"
                width={240}
                tickLine={false}
                axisLine={false}
                tickMargin={12}
                tick={{ fill: "#0f172a", fontSize: 11 }}
              />
              <Tooltip content={renderTooltip} />
              <Legend
                verticalAlign="bottom"
                align="center"
                height={40}
                wrapperStyle={{ bottom: 0, left: 0, right: 0, paddingTop: 8 }}
              />
              {CUSTOMER_STACK_SERIES.map((seriesName) => (
                <Bar
                  key={seriesName}
                  dataKey={seriesName}
                  stackId="customer-stack"
                  fill={CUSTOMER_STACK_COLORS[seriesName]}
                  barSize={14}
                >
                  {rows.map((entry, index) => (
                    <Cell
                      key={`${entry.customer}-${seriesName}-${index}`}
                      className={onBarClick ? "cursor-pointer" : undefined}
                      onClick={() => onBarClick?.(entry, seriesName)}
                    />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

type OverdueTrackingRow = {
  age_bucket: string;
  total: number;
  [assignee: string]: string | number;
};

const OverdueTrackingCard: React.FC<{
  title: string;
  rows: OverdueTrackingRow[];
  assignees: string[];
  isLoading: boolean;
  error: Error | null;
  emptyMessage: string;
  className?: string;
  onBarClick?: (row: OverdueTrackingRow, assignee: string) => void;
}> = ({ title, rows, assignees, isLoading, error, emptyMessage, className, onBarClick }) => {
  const cardClassName = `border-slate-200/70 bg-white/85 shadow-sm backdrop-blur ${className ?? ""}`;

  if (isLoading) {
    return (
      <Card className={cardClassName}>
        <CardHeader className="pb-2">
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cardClassName}>
        <CardHeader className="pb-2">
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load data.</span>
        </CardContent>
      </Card>
    );
  }

  if (!rows.length || !assignees.length) {
    return (
      <Card className={cardClassName}>
        <CardHeader className="pb-2">
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  const renderTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload?: OverdueTrackingRow }>;
  }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;

    return (
      <div className="rounded-xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
        <div className="text-sm font-semibold text-slate-900">{row.age_bucket}</div>
        <div className="mt-2 space-y-1 text-sm">
          {assignees.map((assignee) => (
            <div key={assignee} className="flex items-center justify-between gap-4">
              <span className="text-slate-500">{assignee}</span>
              <span className="font-medium text-slate-900">
                {formatCount(Number(row[assignee] ?? 0))}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 border-t pt-2">
            <span className="text-slate-500">Total</span>
            <span className="font-semibold text-slate-900">{formatCount(row.total)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className={cardClassName}>
      <CardHeader className="pb-1">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              margin={{ top: 20, right: 24, left: 16, bottom: 48 }}
              barCategoryGap="35%"
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis
                dataKey="age_bucket"
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                interval={0}
                label={{
                  value: "Days",
                  position: "bottom",
                  offset: 8,
                  fontSize: 11,
                }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fill: "#64748b", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                label={{
                  value: "Ticket count",
                  angle: -90,
                  position: "left",
                  offset: 12,
                  style: { textAnchor: "middle", fontSize: 11, fill: "#64748b" },
                }}
              />
              <Tooltip content={renderTooltip} />
              <Legend
                verticalAlign="bottom"
                align="center"
                height={40}
                wrapperStyle={{ bottom: 0, left: 0, right: 0, paddingTop: 8 }}
              />
              {assignees.map((assignee, index) => (
                <Bar
                  key={assignee}
                  dataKey={assignee}
                  stackId="overdue"
                  fill={
                    OVERDUE_ASSIGNEE_COLORS[assignee] ??
                    OVERDUE_ASSIGNEE_FALLBACK_COLORS[
                      index % OVERDUE_ASSIGNEE_FALLBACK_COLORS.length
                    ]
                  }
                  barSize={14}
                >
                  {rows.map((entry, rowIndex) => (
                    <Cell
                      key={`${entry.age_bucket}-${assignee}-${rowIndex}`}
                      className={onBarClick ? "cursor-pointer" : undefined}
                      onClick={() => onBarClick?.(entry, assignee)}
                    />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const AssignmentActivityCard: React.FC<{
  title: string;
  description: string;
  rows: AssignmentActivityRow[];
  assignees: string[];
  isLoading: boolean;
  error: Error | null;
  emptyMessage: string;
  className?: string;
  onBarClick?: (row: AssignmentActivityRow, assignee: string) => void;
}> = ({ title, description, rows, assignees, isLoading, error, emptyMessage, className, onBarClick }) => {
  const cardClassName = `border-slate-200/70 bg-white/85 shadow-sm backdrop-blur ${className ?? ""}`;

  if (isLoading) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load data.</span>
        </CardContent>
      </Card>
    );
  }

  if (!rows.length || !assignees.length) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  const renderTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload?: AssignmentActivityRow }>;
  }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;

    return (
      <div className="rounded-xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur">
        <div className="text-sm font-semibold text-slate-900">{String(row.month)}</div>
        <div className="mt-2 space-y-1 text-sm">
          {assignees.map((assignee) => (
            <div key={assignee} className="flex items-center justify-between gap-4">
              <span className="text-slate-500">{assignee}</span>
              <span className="font-medium text-slate-900">{formatCount(Number(row[assignee] ?? 0))}</span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-4 border-t pt-2">
            <span className="text-slate-500">Total</span>
            <span className="font-semibold text-slate-900">{formatCount(row.total)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className={cardClassName}>
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[360px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={rows}
              margin={{ top: 24, right: 36, left: 40, bottom: 48 }}
              barCategoryGap="30%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tick={{ fill: "#64748b", fontSize: 12 }}
              />
              <YAxis
                type="category"
                dataKey="month"
                width={76}
                tickLine={false}
                axisLine={false}
                tickMargin={10}
                tick={{ fill: "#0f172a", fontSize: 11 }}
              />
              <Tooltip content={renderTooltip} />
              <Legend
                verticalAlign="bottom"
                align="center"
                height={40}
                wrapperStyle={{ bottom: 0, left: 0, right: 0, paddingTop: 8 }}
              />
              {assignees.map((assignee, index) => (
                <Bar
                  key={assignee}
                  dataKey={assignee}
                  stackId="assignment-activity"
                  fill={ASSIGNMENT_ACTIVITY_COLORS[index % ASSIGNMENT_ACTIVITY_COLORS.length]}
                  barSize={14}
                >
                  {rows.map((entry, rowIndex) => (
                    <Cell
                      key={`${entry.month_key}-${assignee}-${rowIndex}`}
                      className={onBarClick ? "cursor-pointer" : undefined}
                      onClick={() => onBarClick?.(entry, assignee)}
                    />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const StackedBarCard: React.FC<{
  title: string;
  description: string;
  rows: Record<string, string | number>[];
  series: string[];
  xKey: string;
  isLoading: boolean;
  error: Error | null;
  emptyMessage: string;
  horizontal?: boolean;
  className?: string;
  onBarClick?: (row: Record<string, string | number>, series: string) => void;
}> = ({
  title,
  description,
  rows,
  series,
  xKey,
  isLoading,
  error,
  emptyMessage,
  horizontal = false,
  className,
  onBarClick,
}) => {
  const cardClassName = `border-slate-200/70 bg-white/85 shadow-sm backdrop-blur ${className ?? ""}`;
  if (isLoading) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading data...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load data.</span>
        </CardContent>
      </Card>
    );
  }

  if (!rows.length || !series.length) {
    return (
      <Card className={cardClassName}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cardClassName}>
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className={horizontal ? "h-[360px]" : "h-[320px]"}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout={horizontal ? "vertical" : "horizontal"}
              margin={{ top: 16, right: 24, bottom: 24, left: 16 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              {horizontal ? (
                <>
                  <XAxis
                    type="number"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <YAxis
                    type="category"
                    dataKey={xKey}
                    width={180}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={12}
                    tick={{ fill: "#0f172a", fontSize: 12 }}
                  />
                </>
              ) : (
                <>
                  <XAxis
                    dataKey={xKey}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                    interval={0}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                </>
              )}
              <Tooltip formatter={(value: number) => formatCount(value)} />
              <Legend
                verticalAlign="bottom"
                align="center"
                height={40}
                wrapperStyle={{ bottom: 0, left: 0, right: 0, paddingTop: 8 }}
              />
              {series.map((name, index) => (
                <Bar
                  key={name}
                  dataKey={name}
                  stackId="stack"
                  fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                  radius={index === series.length - 1 ? [8, 8, 0, 0] : 0}
                >
                  {rows.map((entry, rowIndex) => (
                    <Cell
                      key={`${String(entry[xKey])}-${name}-${rowIndex}`}
                      className={onBarClick ? "cursor-pointer" : undefined}
                      onClick={() => onBarClick?.(entry, name)}
                    />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

const HelpdeskDashboardPage: React.FC = () => {
  const [filters, setFilters] = React.useState<FiltersState>(getDefaultFilters);
  const [ticketDrilldown, setTicketDrilldown] = React.useState<TicketDrilldownState | null>(null);
  const [showFilters, setShowFilters] = React.useState(false);
  const [showOperational, setShowOperational] = React.useState(false);
  const [showAnalysis, setShowAnalysis] = React.useState(false);
  const emptyFilters = React.useMemo<HelpdeskDashboardFilters>(() => ({}), []);

  const apiFilters = React.useMemo(
    () => ({
      startDate: normalizeText(filters.startDate),
      endDate: normalizeText(filters.endDate),
      assignee: normalizeSelection(filters.assignee),
      salesperson: normalizeSelection(filters.salesperson),
      customer: normalizeSelection(filters.customer),
      stage: normalizeSelection(filters.stage),
      ticketType: normalizeSelection(filters.ticketType),
      tags: normalizeSelection(filters.tags),
    }),
    [filters]
  );

  const summaryQuery = useHelpdeskSummary(apiFilters);
  const monthlyTrendQuery = useHelpdeskMonthlyTrend(apiFilters);
  const stageDistributionQuery = useHelpdeskStageDistribution(apiFilters);
  const issueTypesQuery = useHelpdeskIssueTypes(apiFilters);
  const subTypesQuery = useHelpdeskSubTypes(apiFilters);
  const assigneesMonthlyQuery = useHelpdeskAssigneesMonthly(apiFilters);
  const assigneesOverallQuery = useHelpdeskAssigneesOverall(apiFilters);
  const topProductsQuery = useHelpdeskTopProducts(apiFilters);
  const topCustomersQuery = useHelpdeskTopCustomers(apiFilters);
  const openAgingQuery = useHelpdeskOpenAging(apiFilters);
  const assigneeOptionsQuery = useHelpdeskAssigneesOverall(emptyFilters);
  const salespersonOptionsQuery = useHelpdeskSalespersonsOverall(emptyFilters);
  const customerOptionsQuery = useHelpdeskTopCustomers(emptyFilters);
  const stageOptionsQuery = useHelpdeskStageDistribution(emptyFilters);
  const ticketTypeOptionsQuery = useHelpdeskIssueTypes(emptyFilters);
  const tagOptionsQuery = useHelpdeskTopProducts(emptyFilters);

  const summary = summaryQuery.data;

  const monthlyTrendRows = React.useMemo(() => monthlyTrendQuery.data ?? [], [monthlyTrendQuery.data]);
  const stageRows = React.useMemo(() => stageDistributionQuery.data ?? [], [stageDistributionQuery.data]);
  const issueTypeRows = React.useMemo(() => issueTypesQuery.data ?? [], [issueTypesQuery.data]);
  const subTypeRows = React.useMemo(() => subTypesQuery.data ?? [], [subTypesQuery.data]);
  const assigneesOverallRows = React.useMemo(() => assigneesOverallQuery.data ?? [], [assigneesOverallQuery.data]);
  const topProductsRows = React.useMemo(() => topProductsQuery.data ?? [], [topProductsQuery.data]);
  const topCustomersRows = React.useMemo(() => topCustomersQuery.data ?? [], [topCustomersQuery.data]);
  const openAgingRows = React.useMemo(() => openAgingQuery.data ?? [], [openAgingQuery.data]);
  const assigneesMonthlyRows = React.useMemo(() => assigneesMonthlyQuery.data ?? [], [assigneesMonthlyQuery.data]);
  const ticketDrilldownFilters = React.useMemo(
    () =>
      ticketDrilldown
        ? {
            ...apiFilters,
            ...ticketDrilldown.filters,
          }
        : null,
    [apiFilters, ticketDrilldown]
  );
  const ticketDrilldownQuery = useHelpdeskTickets(
    ticketDrilldownFilters ?? ({} as HelpdeskTicketDrilldownFilters),
    Boolean(ticketDrilldownFilters)
  );
  const assigneeOptions = React.useMemo(
    () => ["All", ...(assigneeOptionsQuery.data ?? []).map((row) => row.label)],
    [assigneeOptionsQuery.data]
  );
  const salespersonOptions = React.useMemo(
    () => ["All", ...(salespersonOptionsQuery.data ?? []).map((row) => row.label)],
    [salespersonOptionsQuery.data]
  );
  const customerOptions = React.useMemo(
    () => ["All", ...uniqueSortedValues((customerOptionsQuery.data ?? []).map((row) => row.customer))],
    [customerOptionsQuery.data]
  );
  const stageOptions = React.useMemo(
    () => ["All", ...(stageOptionsQuery.data ?? []).map((row) => row.label)],
    [stageOptionsQuery.data]
  );
  const ticketTypeOptions = React.useMemo(
    () => ["All", ...(ticketTypeOptionsQuery.data ?? []).map((row) => row.label)],
    [ticketTypeOptionsQuery.data]
  );
  const tagOptions = React.useMemo(
    () => ["All", ...uniqueSortedValues((tagOptionsQuery.data ?? []).map((row) => row.label))],
    [tagOptionsQuery.data]
  );

  const assigneeMonthlyStack = React.useMemo(() => {
    const assigneeSet = new Set<string>();
    const rowsByMonth = new Map<string, AssignmentActivityRow>();

    for (const row of assigneesMonthlyRows as AssigneeMonthlyPoint[]) {
      assigneeSet.add(row.assigned_to);
      const existing = rowsByMonth.get(row.month) ?? {
        month: getMonthLabel(row.month),
        month_key: row.month,
        total: 0,
      };

      existing[row.assigned_to] = Number(existing[row.assigned_to] ?? 0) + row.ticket_count;
      existing.total += row.ticket_count;
      rowsByMonth.set(row.month, existing);
    }

    const assignees = Array.from(assigneeSet).sort((left, right) => left.localeCompare(right));
    const rows = Array.from(rowsByMonth.values())
      .sort((left, right) => String(right.month_key).localeCompare(String(left.month_key)))
      .map((row) => {
        const next: AssignmentActivityRow = {
          ...row,
          month: row.month,
          month_key: row.month_key,
          total: row.total,
        };
        for (const assignee of assignees) {
          if (typeof next[assignee] !== "number") {
            next[assignee] = 0;
          }
        }
        return next;
      });

    return { rows, assignees };
  }, [assigneesMonthlyRows]);

  const overdueTracking = React.useMemo(() => {
    const assigneeSet = new Set<string>();
    const bucketMap = new Map<string, OverdueTrackingRow>();

    for (const bucket of OVERDUE_BUCKET_ORDER) {
      bucketMap.set(bucket, {
        age_bucket: bucket,
        total: 0,
      });
    }

    for (const row of openAgingRows as OpenAgingPoint[]) {
      if (!OVERDUE_BUCKET_ORDER.includes(row.age_bucket)) continue;
      assigneeSet.add(row.assigned_to);

      const existing = bucketMap.get(row.age_bucket) ?? {
        age_bucket: row.age_bucket,
        total: 0,
      };
      existing[row.assigned_to] = Number(existing[row.assigned_to] ?? 0) + row.ticket_count;
      existing.total += row.ticket_count;
      bucketMap.set(row.age_bucket, existing);
    }

    const assignees = [
      ...Object.keys(OVERDUE_ASSIGNEE_COLORS).filter((name) => assigneeSet.has(name)),
      ...Array.from(assigneeSet)
        .filter((name) => !(name in OVERDUE_ASSIGNEE_COLORS))
        .sort((left, right) => left.localeCompare(right)),
    ];

    const rows = OVERDUE_BUCKET_ORDER.map((bucket) => {
      const current = bucketMap.get(bucket) ?? { age_bucket: bucket, total: 0 };
      const next: OverdueTrackingRow = {
        ...current,
        age_bucket: bucket,
        total: current.total ?? 0,
      };
      for (const assignee of assignees) {
        if (typeof next[assignee] !== "number") {
          next[assignee] = 0;
        }
      }
      return next;
    });

    return { rows, assignees };
  }, [openAgingRows]);

  const customerStack = React.useMemo(() => {
    const rowsByCustomer = new Map<string, CustomerStackedRow>();

    for (const row of topCustomersRows as CustomerIssueTypePoint[]) {
      const seriesName = normalizeCustomerTicketType(row.ticket_type);
      const existing = rowsByCustomer.get(row.customer) ?? {
        customer: row.customer,
        License: 0,
        "Product Issue": 0,
        "Question/Enquiry": 0,
        total: 0,
      };

      existing[seriesName] += row.ticket_count;
      existing.total += row.ticket_count;
      rowsByCustomer.set(row.customer, existing);
    }

    return Array.from(rowsByCustomer.values())
      .sort((left, right) => right.total - left.total || left.customer.localeCompare(right.customer))
      .slice(0, 10);
  }, [topCustomersRows]);

  const ticketTableColumns = React.useMemo(
    () => [
      { key: "subject", header: "Subject", widthClassName: "min-w-[280px]" },
      { key: "customer", header: "Customer", widthClassName: "min-w-[180px]" },
      { key: "assigned_to", header: "Assignee", widthClassName: "min-w-[140px]" },
      { key: "salesperson", header: "Salesperson", widthClassName: "min-w-[160px]" },
      { key: "stage", header: "Stage", widthClassName: "min-w-[120px]" },
      { key: "tags", header: "Tags", widthClassName: "min-w-[180px]" },
      { key: "ticket_type", header: "Type", widthClassName: "min-w-[140px]" },
      { key: "sub_type", header: "Sub Type", widthClassName: "min-w-[140px]" },
      {
        key: "created_on",
        header: "Created On",
        sortType: "date" as const,
        getValue: (row: MonthlyTrendTicketInfo) => row.created_on,
        render: (row: MonthlyTrendTicketInfo) => formatDateTimeLabel(row.created_on),
        widthClassName: "min-w-[170px]",
      },
    ],
    []
  );

  const openTicketDrilldown = (title: string, filters: HelpdeskTicketDrilldownFilters) => {
    setTicketDrilldown({ title, filters });
  };

  const openKpiTicketDrilldown = (
    title: string,
    ticketScope: NonNullable<HelpdeskTicketDrilldownFilters["ticketScope"]>
  ) => {
    openTicketDrilldown(title, { ticketScope });
  };

  const closeTicketDrilldown = () => setTicketDrilldown(null);

  const onClearFilters = () => setFilters(getDefaultFilters());
  const hasFilters = Boolean(
    apiFilters.startDate ||
      apiFilters.endDate ||
      apiFilters.assignee ||
      apiFilters.salesperson ||
      apiFilters.customer ||
      apiFilters.stage ||
      apiFilters.ticketType ||
      apiFilters.tags
  );

  return (
    <>
      <Dialog
        open={Boolean(ticketDrilldown)}
        onOpenChange={(open) => {
          if (!open) closeTicketDrilldown();
        }}
      >
        <DialogContent className="w-[min(96vw,1600px)] max-w-none">
          <DialogHeader>
            <DialogTitle>{ticketDrilldown?.title ?? "Ticket Drilldown"}</DialogTitle>
            <DialogDescription>Ticket details for the selected chart point and current filters.</DialogDescription>
          </DialogHeader>
          {ticketDrilldownQuery.isLoading ? (
            <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
              Loading ticket list...
            </div>
          ) : ticketDrilldownQuery.error ? (
            <div className="flex h-[220px] items-center justify-center gap-2 text-sm text-rose-700">
              <AlertCircle className="h-4 w-4" />
              Failed to load ticket list.
            </div>
          ) : (
            <div className="mt-4 max-h-[68vh] overflow-auto">
              <SharedTable
                rows={ticketDrilldownQuery.data ?? []}
                cols={ticketTableColumns as any}
                emptyText="No tickets found for the selected month."
                defaultSort={{ key: "created_on", dir: "desc" }}
                className="border-slate-200"
                onRowClick={(row) => {
                  window.open(buildOdooTicketUrl(Number(row.ticket_id)), "_blank", "noopener,noreferrer");
                }}
              />
              <div className="mt-2 text-xs text-slate-500">Click a row to open the ticket in Odoo.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="relative space-y-6">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-[radial-gradient(circle_at_top_left,_rgba(15,118,110,0.18),_transparent_35%),radial-gradient(circle_at_top_right,_rgba(37,99,235,0.14),_transparent_28%),linear-gradient(to_bottom,_rgba(248,250,252,0.96),_transparent)]" />

      <PageHeader
        title="Helpdesk Dashboard"
        description="Ticket volume, stage mix, assignee load, tag activity, and aging metrics sourced directly from the BI database."
      />

      <section className="grid gap-6 xl:grid-cols-4">
        <Card className="border-slate-200/70 bg-white/85 shadow-sm backdrop-blur xl:col-span-4">
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Filters</CardTitle>
              <CardDescription>
                All filters are optional. Leaving them blank returns all available ticket data.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onClearFilters} disabled={!hasFilters}>
                <FilterX className="mr-2 h-4 w-4" />
                Clear filters
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowFilters((current) => !current)}>
                {showFilters ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
                {showFilters ? "Hide" : "Show"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {showFilters ? (
              <>
                {summaryQuery.error ? (
                  <div className="mb-4 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    <AlertCircle className="h-4 w-4" />
                    Summary data failed to load. The charts below may still render if their requests succeed.
                  </div>
                ) : null}
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Start date</span>
                    <input
                      type="date"
                      value={filters.startDate ?? ""}
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          startDate: event.target.value || undefined,
                        }))
                      }
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-slate-400"
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">End date</span>
                    <input
                      type="date"
                      value={filters.endDate ?? ""}
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          endDate: event.target.value || undefined,
                        }))
                      }
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm shadow-sm outline-none ring-0 transition focus:border-slate-400"
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Assignee</span>
                    <Select
                      value={filters.assignee ?? "All"}
                      onValueChange={(value) =>
                        setFilters((current) => ({
                          ...current,
                          assignee: value,
                        }))
                      }
                    >
                      <SelectTrigger className="h-10 w-full rounded-md border-slate-200 bg-white text-sm shadow-sm">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        {assigneeOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  <ComboboxFilter
                    label="Salesperson"
                    value={filters.salesperson}
                    options={salespersonOptions.filter((option) => option !== "All")}
                    placeholder="All"
                    searchPlaceholder="Search salesperson..."
                    emptyMessage="No salesperson found."
                    onSelect={(value) =>
                      setFilters((current) => ({
                        ...current,
                        salesperson: value ?? "All",
                      }))
                    }
                  />
                  <ComboboxFilter
                    label="Customer"
                    value={filters.customer}
                    options={customerOptions.filter((option) => option !== "All")}
                    placeholder="All"
                    searchPlaceholder="Search customer..."
                    emptyMessage="No customer found."
                    onSelect={(value) =>
                      setFilters((current) => ({
                        ...current,
                        customer: value ?? "All",
                      }))
                    }
                  />
                  <ComboboxFilter
                    label="Tags"
                    value={filters.tags}
                    options={tagOptions.filter((option) => option !== "All")}
                    placeholder="All"
                    searchPlaceholder="Search tag..."
                    emptyMessage="No tag found."
                    onSelect={(value) =>
                      setFilters((current) => ({
                        ...current,
                        tags: value ?? "All",
                      }))
                    }
                  />
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Stage</span>
                    <Select
                      value={filters.stage ?? "All"}
                      onValueChange={(value) =>
                        setFilters((current) => ({
                          ...current,
                          stage: value,
                        }))
                      }
                    >
                      <SelectTrigger className="h-10 w-full rounded-md border-slate-200 bg-white text-sm shadow-sm">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        {stageOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Ticket type</span>
                    <Select
                      value={filters.ticketType ?? "All"}
                      onValueChange={(value) =>
                        setFilters((current) => ({
                          ...current,
                          ticketType: value,
                        }))
                      }
                    >
                      <SelectTrigger className="h-10 w-full rounded-md border-slate-200 bg-white text-sm shadow-sm">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        {ticketTypeOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                </div>
                <div className="flex items-center gap-2 border-t border-slate-200/80 pt-3 text-xs text-slate-500">
                  <span className="font-medium uppercase tracking-[0.18em] text-slate-400">API</span>
                  <span className="min-w-0 truncate">
                    Data is read directly from the BI database. Empty values are normalized to Unknown or Unassigned.
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                <span>Filters are hidden.</span>
                <span className="text-xs uppercase tracking-[0.18em] text-slate-400">Use Show to expand</span>
              </div>
            )}
          </CardContent>
        </Card>

        <section className="grid gap-4 md:grid-cols-2 xl:col-span-4 xl:grid-cols-4">
          {[
            {
              label: "Total tickets",
              value: summary?.total_tickets,
              icon: CircleDot,
              tone: "from-slate-50 to-white",
              scope: "all" as const,
            },
            {
              label: "Open tickets",
              value: summary?.open_tickets,
              icon: CircleSlash2,
              tone: "from-emerald-50 to-white",
              scope: "open" as const,
            },
            {
              label: "Solved tickets",
              value: summary?.solved_tickets,
              icon: CircleDot,
              tone: "from-blue-50 to-white",
              scope: "solved" as const,
            },
            {
              label: "Overdue tickets",
              value: summary?.overdue_tickets,
              icon: AlertCircle,
              tone: "from-rose-50 to-white",
              scope: "overdue" as const,
            },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Card
                key={item.label}
                role="button"
                tabIndex={0}
                aria-label={`${item.label}. Open ticket list`}
                onClick={() => openKpiTicketDrilldown(item.label, item.scope)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openKpiTicketDrilldown(item.label, item.scope);
                  }
                }}
                className={`group cursor-pointer border-slate-200/70 bg-gradient-to-br ${item.tone} shadow-sm backdrop-blur transition duration-150 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2`}
              >
                <CardContent className="flex items-center justify-between gap-4 p-5">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {item.label}
                    </div>
                    <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                      {formatCount(item.value)}
                    </div>
                    <div className="mt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 transition group-hover:text-slate-600">
                      View tickets
                    </div>
                  </div>
                  <div className="rounded-2xl bg-white/80 p-3 shadow-sm">
                    <Icon className="h-5 w-5 text-slate-700" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </section>
      </section>

      <section className="space-y-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">Operational</h2>
            <p className="text-sm text-slate-600">Live monitoring and workload tracking.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowOperational((current) => !current)}>
            {showOperational ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
            {showOperational ? "Hide" : "Show"}
          </Button>
        </div>

        {showOperational ? (
          <>
            <div className="grid gap-6 xl:grid-cols-2">
              <Card className="border-slate-200/70 bg-white/85 shadow-sm backdrop-blur">
                <CardHeader className="pb-2">
                  <CardTitle>Monthly Trend by Ticket Count</CardTitle>
                  <CardDescription>Ticket volume by created month. Click a month dot to view tickets.</CardDescription>
                </CardHeader>
                <CardContent>
                  {monthlyTrendQuery.isLoading ? (
                    <div className="flex h-[160px] items-center justify-center text-sm text-muted-foreground">
                      Loading monthly trend...
                    </div>
                  ) : monthlyTrendQuery.error ? (
                    <div className="flex h-[160px] items-center justify-center gap-2 text-sm text-rose-700">
                      <AlertCircle className="h-4 w-4" />
                      Failed to load monthly trend.
                    </div>
                  ) : !monthlyTrendRows.length ? (
                    <div className="flex h-[160px] items-center justify-center text-sm text-muted-foreground">
                      No monthly trend data for the selected filters.
                    </div>
                  ) : (
                    <div className="h-[160px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={monthlyTrendRows}
                          margin={{ top: 16, right: 24, bottom: 20, left: 16 }}
                          onClick={(state: any) => {
                            const payload = state?.activePayload?.[0]?.payload;
                            if (payload?.month) {
                              openTicketDrilldown(`Monthly Trend: ${formatMonthLabel(payload.month)}`, {
                                month: payload.month,
                              });
                            }
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis
                            dataKey="month"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={10}
                            tick={{ fill: "#64748b", fontSize: 12 }}
                            tickFormatter={formatMonthLabel}
                          />
                          <YAxis
                            width={56}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tick={{ fill: "#64748b", fontSize: 12 }}
                          />
                          <Tooltip formatter={(value: number) => formatCount(value)} />
                          <Line
                            type="linear"
                            dataKey="ticket_count"
                            stroke="#0f766e"
                            strokeWidth={3}
                            dot={{ r: 4, fill: "#0f766e", stroke: "#ffffff", strokeWidth: 2 }}
                            activeDot={{ r: 5 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>
              <OverdueTrackingCard
                title="Overdue Tracking (Days To Resolve)"
                rows={overdueTracking.rows}
                assignees={overdueTracking.assignees}
                isLoading={openAgingQuery.isLoading}
                error={openAgingQuery.error ?? null}
                emptyMessage="No overdue tracking data for the selected filters."
                className=""
                onBarClick={(row, assignee) => {
                  openTicketDrilldown(`Overdue Tracking: ${row.age_bucket} / ${assignee}`, {
                    ageBucket: row.age_bucket,
                    assignee,
                  });
                }}
              />
            </div>

            <div className="grid gap-6 xl:grid-cols-4">
              <AssignmentActivityCard
                title="Assignment Activity"
                description="Monthly ticket count stacked by assignee."
                rows={assigneeMonthlyStack.rows}
                assignees={assigneeMonthlyStack.assignees}
                isLoading={assigneesMonthlyQuery.isLoading}
                error={assigneesMonthlyQuery.error ?? null}
                emptyMessage="No assignee activity data for the selected filters."
                className="xl:col-span-2"
                onBarClick={(row, assignee) => {
                  openTicketDrilldown(`Assignment Activity: ${row.month} / ${assignee}`, {
                    month: row.month_key,
                    assignee,
                  });
                }}
              />
              <DonutCard
                title="Stage Distribution"
                description="Ticket count by current stage."
                data={stageRows}
                isLoading={stageDistributionQuery.isLoading}
                error={stageDistributionQuery.error ?? null}
                emptyMessage="No stage data for the selected filters."
                className=""
                palette="stage"
                smartLabelLayout
                onSliceClick={(label) => {
                  openTicketDrilldown(`Stage: ${label}`, { stage: label });
                }}
              />
              <DonutCard
                title="Top Assignees"
                description="Total ticket count by assignee."
                data={assigneesOverallRows}
                isLoading={assigneesOverallQuery.isLoading}
                error={assigneesOverallQuery.error ?? null}
                emptyMessage="No overall assignee data for the selected filters."
                palette="generic"
                className=""
                onSliceClick={(label) => {
                  openTicketDrilldown(`Assignee: ${label}`, { assignee: label });
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
            <span>Operational section is hidden.</span>
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400">Use Show to expand</span>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-slate-900">Analysis</h2>
            <p className="text-sm text-slate-600">Breakdowns for diagnosis and root-cause inspection.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowAnalysis((current) => !current)}>
            {showAnalysis ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
            {showAnalysis ? "Hide" : "Show"}
          </Button>
        </div>

        {showAnalysis ? (
          <>
            <div className="grid gap-6 xl:grid-cols-2">
              <CustomerStackedBarCard
                title="Top Enquiry Customer by Ticket Count"
                description="Top customers stacked by ticket type."
                rows={customerStack}
                isLoading={topCustomersQuery.isLoading}
                error={topCustomersQuery.error ?? null}
                emptyMessage="No customer breakdown data for the selected filters."
                className="xl:col-span-1"
                onBarClick={(row, series) => {
                  openTicketDrilldown(`Customer: ${row.customer} / ${series}`, {
                    customer: row.customer,
                    ticketType: series,
                  });
                }}
              />
              <TopProductsPieCard
                title="Top Enquiry Products by Ticket Count"
                description="Top 4 products shown directly. Remaining products are grouped into Other."
                data={topProductsRows}
                isLoading={topProductsQuery.isLoading}
                error={topProductsQuery.error ?? null}
                emptyMessage="No tag data for the selected filters."
                className="xl:col-span-1"
                onSliceClick={(label) => {
                  openTicketDrilldown(`Tag: ${label}`, { tags: label });
                }}
              />
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <DonutCard
                title="Issue Type by Ticket Count"
                description="Ticket count by issue type."
                data={issueTypeRows}
                isLoading={issueTypesQuery.isLoading}
                error={issueTypesQuery.error ?? null}
                emptyMessage="No issue type data for the selected filters."
                palette="issue-type"
                smartLabelLayout
                legendOnRight
                legendFontSize={12}
                legendPanelWidth={280}
                className="xl:col-span-1"
                onSliceClick={(label) => {
                  openTicketDrilldown(`Issue Type: ${label}`, { ticketType: label });
                }}
              />
              <DonutCard
                title="Issue Type - Details"
                description=""
                data={subTypeRows}
                isLoading={subTypesQuery.isLoading}
                error={subTypesQuery.error ?? null}
                emptyMessage="No sub type data for the selected filters."
                palette="generic"
                showLegend={false}
                labelFontSize={12}
                className="xl:col-span-1"
                onSliceClick={(label) => {
                  openTicketDrilldown(`Sub Type: ${label}`, { subType: label });
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
            <span>Analysis section is hidden.</span>
            <span className="text-xs uppercase tracking-[0.18em] text-slate-400">Use Show to expand</span>
          </div>
        )}
      </section>

      </div>
    </>
  );
};

export default HelpdeskDashboardPage;
